/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import type { CSSProperties } from "react";
import { GripVertical, Lightbulb, Plus } from "lucide-react";
import { Button } from "../components/ui/Button";
import { useChrome } from "../contexts/ChromeContext";

type Field = {
  question: string;
  type: string;
  /** The agent that consumes this answer — surfaced so authors see why a field exists. */
  neededBy?: string;
  options?: string[];
};

const FIELDS: Field[] = [
  { question: "What brings your family in right now?", type: "Long text", neededBy: "matching" },
  {
    question: "Who is the care for?",
    type: "Single select",
    options: ["A child", "A teen", "A parent", "Whole family"],
  },
  { question: "Child's age", type: "Number", neededBy: "matching" },
  {
    question: "What are you noticing?",
    type: "Multi select",
    neededBy: "matching",
    options: ["Big emotions", "Tough behaviors", "School stress", "Family conflict"],
  },
  {
    question: "Your insurance",
    type: "Payer lookup",
    neededBy: "eligibility",
    options: ["Wired to live eligibility check"],
  },
  { question: "Preferred session times", type: "Availability grid" },
];

const WIRING: { key: string; value: string; tone?: "agent" | "rule" | "mono" }[] = [
  { key: "Writes to", value: "referrals", tone: "mono" },
  { key: "Triggers", value: "matching", tone: "agent" },
  { key: "Eligibility", value: "auto-check", tone: "rule" },
  { key: "Live at", value: "/refer", tone: "mono" },
];

export function Forms() {
  useChrome({ title: "Forms" }, []);
  return (
    <div className="mx-auto max-w-5xl space-y-8 pb-4">
      <header className="enter max-w-2xl">
        <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
          Family intake screener
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-text-secondary">
          The form writes straight to the semantic layer — no Typeform, no Zapier. Fields the
          matching agent needs are marked.
        </p>
      </header>

      <div className="grid gap-5 lg:grid-cols-[1fr_280px]">
        <div className="space-y-3">
          {FIELDS.map((field, index) => (
            <FieldCard key={field.question} field={field} index={index} />
          ))}
          <button
            type="button"
            className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-border py-3 text-sm font-medium text-text-tertiary transition-colors hover:border-border-hover hover:text-text-secondary"
          >
            <Plus size={16} />
            Add field
          </button>
        </div>

        <aside className="space-y-4">
          <div className="border border-border bg-bg-elevated shadow-card rounded-2xl p-4">
            <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-text-tertiary">
              Wiring
            </p>
            <dl className="mt-3 space-y-2.5">
              {WIRING.map((row) => (
                <div key={row.key} className="flex items-center justify-between gap-3 text-sm">
                  <dt className="text-[11px] uppercase tracking-[0.07em] text-text-tertiary">
                    {row.key}
                  </dt>
                  <dd
                    className={
                      row.tone === "agent"
                        ? "inline-flex items-center gap-1.5 text-[13px] font-medium text-accent"
                        : row.tone === "rule"
                          ? "inline-flex items-center gap-1.5 text-[13px] font-medium text-text-secondary"
                          : "font-mono text-[12px] text-text-secondary"
                    }
                  >
                    {(row.tone === "agent" || row.tone === "rule") && (
                      <span className="size-1.5 rounded-full bg-current" />
                    )}
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="border border-border bg-bg-elevated shadow-card rounded-2xl p-4">
            <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-accent">
              <Lightbulb size={12} />
              Agent suggestion
            </p>
            <p className="mt-2.5 text-[13px] leading-relaxed text-text-secondary">
              3% of families drop at “insurance.” Moving it after matching should lift completion
              about 9%.
            </p>
            <Button size="sm" className="mt-3 w-full">
              Apply change
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
}

function FieldCard({ field, index }: { field: Field; index: number }) {
  return (
    <div
      className="lift border border-border bg-bg-elevated shadow-card flex items-start gap-3 rounded-2xl p-4"
      style={{ "--enter-delay": `${Math.min(index, 8) * 40 + 60}ms` } as CSSProperties}
    >
      <button
        type="button"
        aria-label="Reorder field"
        className="mt-0.5 cursor-grab text-text-tertiary transition-colors hover:text-text-secondary"
      >
        <GripVertical size={16} />
      </button>
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-medium text-text-primary">{field.question}</p>
        <p className="mt-1 text-[11px] uppercase tracking-[0.06em] text-text-tertiary">
          {field.type}
          {field.neededBy !== undefined && (
            <>
              {" · "}
              <span className="font-medium text-accent">needed by {field.neededBy}</span>
            </>
          )}
        </p>
        {field.options !== undefined && (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {field.options.map((option) => (
              <span
                key={option}
                className="rounded-lg border border-border bg-bg-elevated/60 px-2.5 py-1 text-[11.5px] text-text-secondary"
              >
                {option}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
